import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { useAuth } from "@/hooks/use-auth";
import { CheckCircle2, XCircle, Globe, MapPin, Mail, Clock } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/affiliates")({
  component: AffiliatesPage,
});

type Affiliate = {
  id: string;
  user_id: string;
  code: string;
  commission_rate: number;
  status: "pending" | "active" | "suspended" | "rejected";
  payout_email: string | null;
  website: string | null;
  country: string | null;
  notes: string | null;
  review_reason: string | null;
  reviewed_at: string | null;
  created_at: string;
};

function statusVariant(s: Affiliate["status"]) {
  if (s === "active") return "default" as const;
  if (s === "pending") return "secondary" as const;
  return "destructive" as const;
}

function AffiliatesPage() {
  const { user } = useAuth();
  const [q, setQ] = useState("");

  const { data: allAffiliates = [], refetch } = useQuery({
    queryKey: ["affiliates_list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliates")
        .select("id, user_id, code, commission_rate, status, payout_email, website, country, notes, review_reason, reviewed_at, created_at")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Affiliate[];
    },
  });

  const affiliates = q.trim()
    ? allAffiliates.filter((a) => {
        const s = q.toLowerCase();
        return (
          a.code.toLowerCase().includes(s) ||
          (a.website ?? "").toLowerCase().includes(s) ||
          (a.country ?? "").toLowerCase().includes(s) ||
          (a.payout_email ?? "").toLowerCase().includes(s)
        );
      })
    : allAffiliates;

  const pending = affiliates.filter((a) => a.status === "pending");
  const active = affiliates.filter((a) => a.status === "active");
  const archived = affiliates.filter((a) => a.status === "rejected" || a.status === "suspended");

  const notify = async (a: Affiliate, action: string, reason: string | null) => {
    try {
      await supabase.from("audit_logs").insert({
        actor_id: user?.id ?? null,
        action: `affiliate.${action}`,
        entity_type: "affiliate",
        entity_id: a.id,
        severity: action === "rejected" || action === "suspended" ? "warning" : "info",
        metadata: { code: a.code, user_id: a.user_id, reason },
      });
    } catch {
      /* non-blocking */
    }
  };

  const updateStatus = async (a: Affiliate, status: Affiliate["status"], reason?: string | null) => {
    const { error } = await supabase
      .from("affiliates")
      .update({
        status,
        review_reason: reason ?? null,
        reviewed_at: new Date().toISOString(),
        reviewed_by: user?.id ?? null,
      })
      .eq("id", a.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    await notify(a, status === "active" ? "approved" : status, reason ?? null);
    toast.success(
      status === "active"
        ? "Affiliate approved — the user will see active status on their dashboard."
        : status === "rejected"
        ? "Application rejected and the user has been notified."
        : `Status set to ${status}.`,
    );
    refetch();
  };

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", commission_rate: 10, payout_email: "" });

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Affiliates"
        description="Review applications, approve or reject affiliates, and manage active partners."
        onNew={() => setOpen(true)}
        newLabel="Add affiliate"
        search={q}
        onSearchChange={setQ}
        searchPlaceholder="Search code, website, country, email…"
        count={allAffiliates.length}
        countLabel="partners"
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Pending" value={allAffiliates.filter((a) => a.status === "pending").length} tone="warning" />
        <StatCard label="Active" value={allAffiliates.filter((a) => a.status === "active").length} tone="success" />
        <StatCard label="Suspended" value={allAffiliates.filter((a) => a.status === "suspended").length} />
        <StatCard label="Rejected" value={allAffiliates.filter((a) => a.status === "rejected").length} />
      </div>

      <Tabs defaultValue="pending">
        <PillTabsList search>
          <PillTabsTrigger value="pending" className="gap-2">
            Review queue
            {pending.length > 0 && <Badge variant="secondary" className="ml-1">{pending.length}</Badge>}
          </PillTabsTrigger>
          <PillTabsTrigger value="active">Active ({active.length})</PillTabsTrigger>
          <PillTabsTrigger value="archived">Rejected / Suspended ({archived.length})</PillTabsTrigger>
          <PillTabsTrigger value="commissions">Commissions</PillTabsTrigger>
        </PillTabsList>


        <TabsContent value="pending" className="mt-4 space-y-3">
          {pending.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center text-sm text-muted-foreground">
                No applications waiting for review.
              </CardContent>
            </Card>
          ) : (
            pending.map((a) => (
              <ReviewCard key={a.id} affiliate={a} onApprove={() => updateStatus(a, "active")} onReject={(reason) => updateStatus(a, "rejected", reason)} />
            ))
          )}
        </TabsContent>

        <TabsContent value="active" className="mt-4">
          <AffiliateTable rows={active} onAction={(a, s, r) => updateStatus(a, s, r)} />
        </TabsContent>

        <TabsContent value="archived" className="mt-4">
          <AffiliateTable rows={archived} onAction={(a, s, r) => updateStatus(a, s, r)} />
        </TabsContent>

        <TabsContent value="commissions" className="mt-4">
          <CommissionsTable />
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add affiliate code</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">For invite-only programs. Paste the user's ID, or have them self-apply.</p>
            <div className="space-y-1.5"><Label className="text-xs">User ID</Label><Input id="aff-user" placeholder="UUID" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="JANE2024" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Commission rate (%)</Label><Input type="number" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: Number(e.target.value) })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Payout email</Label><Input type="email" value={form.payout_email} onChange={(e) => setForm({ ...form, payout_email: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={async () => {
              const uid = (document.getElementById("aff-user") as HTMLInputElement)?.value;
              if (!uid || !form.code) { toast.error("User ID and code required"); return; }
              const { error } = await supabase.from("affiliates").insert({ user_id: uid, code: form.code, commission_rate: form.commission_rate, payout_email: form.payout_email || null, status: "active" });
              if (error) toast.error(error.message); else { refetch(); setOpen(false); toast.success("Affiliate added"); }
            }}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReviewCard({ affiliate, onApprove, onReject }: { affiliate: Affiliate; onApprove: () => void; onReject: (reason: string) => void }) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const submitted = new Date(affiliate.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base font-mono">{affiliate.code}</CardTitle>
            <CardDescription className="flex items-center gap-1.5 mt-1">
              <Clock className="h-3.5 w-3.5" /> Submitted {submitted}
            </CardDescription>
          </div>
          <Badge variant="secondary" className="capitalize">{affiliate.status}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-3 sm:grid-cols-3 text-sm">
          <Field icon={Globe} label="Website" value={affiliate.website} link={affiliate.website ?? undefined} />
          <Field icon={MapPin} label="Country" value={affiliate.country} />
          <Field icon={Mail} label="Payout email" value={affiliate.payout_email} />
        </dl>
        {affiliate.notes && (
          <div className="rounded-md border border-border bg-secondary/30 p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">About the applicant</div>
            <p className="whitespace-pre-wrap">{affiliate.notes}</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button onClick={onApprove} className="gap-2">
            <CheckCircle2 className="h-4 w-4" /> Approve
          </Button>
          <Button variant="destructive" onClick={() => setRejectOpen(true)} className="gap-2">
            <XCircle className="h-4 w-4" /> Reject
          </Button>
        </div>
      </CardContent>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject application</DialogTitle>
            <DialogDescription>The applicant will see this reason on their dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason" className="text-xs">Reason</Label>
            <Textarea id="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Website doesn't match our audience guidelines." maxLength={500} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={!reason.trim()}
              onClick={() => {
                onReject(reason.trim());
                setRejectOpen(false);
                setReason("");
              }}
            >
              Reject application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Field({ icon: Icon, label, value, link }: { icon: React.ComponentType<{ className?: string }>; label: string; value: string | null; link?: string }) {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {label}
      </dt>
      <dd className="mt-1 font-medium truncate">
        {value ? (
          link ? (
            <a href={link} target="_blank" rel="noreferrer" className="text-primary hover:underline">{value}</a>
          ) : value
        ) : <span className="text-muted-foreground">—</span>}
      </dd>
    </div>
  );
}

function AffiliateTable({ rows, onAction }: { rows: Affiliate[]; onAction: (a: Affiliate, status: Affiliate["status"], reason?: string | null) => void }) {
  if (rows.length === 0) {
    return (
      <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Nothing here yet.</CardContent></Card>
    );
  }
  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="w-full overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left text-xs">
          <tr>
            <th className="px-4 py-2">Code</th>
            <th className="hidden sm:table-cell px-4 py-2">Website</th>
            <th className="hidden md:table-cell px-4 py-2">Country</th>
            <th className="px-4 py-2">Rate</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-border">
              <td className="px-4 py-2 font-mono">{a.code}</td>
              <td className="hidden sm:table-cell px-4 py-2 max-w-[200px] truncate text-muted-foreground">{a.website ?? "—"}</td>
              <td className="hidden md:table-cell px-4 py-2 text-muted-foreground">{a.country ?? "—"}</td>
              <td className="px-4 py-2">{a.commission_rate}%</td>
              <td className="px-4 py-2"><Badge variant={statusVariant(a.status)} className="capitalize">{a.status}</Badge></td>
              <td className="px-4 py-2 text-right space-x-1">
                {a.status !== "active" && <Button size="sm" variant="ghost" onClick={() => onAction(a, "active", null)}>Reactivate</Button>}
                {a.status === "active" && <Button size="sm" variant="ghost" onClick={() => onAction(a, "suspended", "Suspended by admin")}>Suspend</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number; tone?: "success" | "warning" }) {
  const toneClass = tone === "success" ? "text-emerald-600" : tone === "warning" ? "text-amber-600" : "";
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
    </div>
  );
}


function CommissionsTable() {
  const { data: commissions = [] } = useQuery({
    queryKey: ["commissions_list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("affiliate_commissions")
        .select("id, amount_cents, status, created_at, affiliate_id, order_id")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as any[];
    },
  });

  return (
    <div className="rounded-lg border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-secondary/50 text-left text-xs">
          <tr>
            <th className="px-4 py-2">Date</th>
            <th className="px-4 py-2">Amount</th>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Order</th>
          </tr>
        </thead>
        <tbody>
          {commissions.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-muted-foreground">No commissions yet.</td></tr>}
          {commissions.map((c) => (
            <tr key={c.id} className="border-t border-border">
              <td className="px-4 py-2 text-muted-foreground">{new Date(c.created_at).toLocaleDateString()}</td>
              <td className="px-4 py-2">${(c.amount_cents / 100).toFixed(2)}</td>
              <td className="px-4 py-2"><Badge variant="outline">{c.status}</Badge></td>
              <td className="px-4 py-2 text-muted-foreground font-mono text-xs">{c.order_id.slice(0, 8)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
